import { useCallback, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useAppNavigate } from '@/lib/query-params'
import { isServiceError } from '@/services/errors'
import { writePendingPayment } from '@/services/pagos'
import type { PaymentKind, PaymentStart } from '@/services/pagos'
import type { Money } from '@/services/types'
import { enablePartial, PAY_ROUTES, paymentErrorMessage } from './shared'

/** Lo que la pantalla de autorización necesita cuando el PMS cobra sin pasar por el banco. */
export interface CompletedPayment {
  kind: PaymentKind
  amount: number
  currency: string
  returnTo: string
  last4: string | null
}

export interface AuthorizeState {
  completed?: CompletedPayment
  last4?: string | null
}

/**
 * Lanza el cobro (E2 → E3) desde la pantalla del formulario. Si el banco pide autorización, guarda el pago en
 * curso (sin datos de tarjeta) y abre la pantalla de autorización; si el PMS lo cobra directamente, va a la
 * misma pantalla en estado autorizado. Un fallo del cobro habilita el pago parcial, como la app actual.
 */
export function usePaymentStart(guestCode: string) {
  const { t } = useTranslation()
  const { go } = useAppNavigate()
  const [starting, setStarting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const start = useCallback(
    async (meta: { kind: PaymentKind; amount: Money; returnTo: string; last4: string | null }, action: () => Promise<PaymentStart>): Promise<boolean> => {
      setStarting(true)
      setError(null)
      try {
        const result = await action()
        if (result.status === 'redirect') {
          writePendingPayment({ guestCode, kind: meta.kind, amount: meta.amount.amount, currency: meta.amount.currency, temporalPaidId: result.temporalPaidId, iframeUrl: result.iframeUrl, returnTo: meta.returnTo, startedAt: new Date().toISOString() })
          go(PAY_ROUTES.authorize, { state: { last4: meta.last4 } satisfies AuthorizeState })
        } else {
          const completed: CompletedPayment = { kind: meta.kind, amount: meta.amount.amount, currency: meta.amount.currency, returnTo: meta.returnTo, last4: meta.last4 }
          go(PAY_ROUTES.authorize, { state: { completed } satisfies AuthorizeState })
        }
        return true
      } catch (e) {
        if (isServiceError(e) && e.code === 'payment_failed') enablePartial(guestCode)
        setError(paymentErrorMessage(e, t))
        return false
      } finally {
        setStarting(false)
      }
    },
    [go, guestCode, t],
  )
  return { start, starting, error, setError }
}
